// Firma / Rollen: derives the design's local config shape (emps, cards, apple,
// escSup, absCustom, roles, perm2, bank + IBANs) from live /core data, and
// translates the design logic's config patches into API calls.
//
// Typing is kept responsive with local overlays (pending edits) and drafts
// (new rows that aren't complete enough to create yet, e.g. a card without 4 digits).
import { PERMISSION_KEYS } from '@rimon/contracts';
import { api, errorText } from '../api/client';
import { tr2 } from '../i18n/lang';
import { live, loadCore } from './compta';
import { cardUsable, expiredCards, expiryLabel, expiryMonth } from './cards';

type Notify = (text: string, type: 'success' | 'warning', ms?: number, undo?: () => void) => void;
const name = (e?: { firstName: string; lastName: string } | null) => (e ? `${e.firstName} ${e.lastName}`.trim() : '');
const splitName = (n: string) => { const p = n.trim().split(/\s+/); return { firstName: p[0] || '', lastName: p.slice(1).join(' ') || '' }; };
const pctIn = (v: string | null) => (v == null ? '' : String(Number(v)).replace('.', ','));
const pctOut = (v: string) => { const n = String(v || '').replace(',', '.'); return /^\d+(\.\d+)?$/.test(n) ? n : null; };
const isMail = (v: string) => /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)*\.[a-z]{2,}$/i.test(v.trim());

/** IBAN check (ISO 13616): country + check digits, length per country (LU 20), mod 97 = 1 – same rule as the server. */
export function validIban(value: string): boolean {
  const iban = value.replace(/\s/g, '').toUpperCase(), LEN: Record<string, number> = { LU: 20, DE: 22, FR: 27, BE: 16, NL: 18, PT: 25, IT: 27, ES: 24, CH: 21, AT: 20 };
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{8,30}$/.test(iban) || (LEN[iban.slice(0, 2)] && iban.length !== LEN[iban.slice(0, 2)])) return false;
  let rest = 0; for (const ch of iban.slice(4) + iban.slice(0, 4)) { const v = /[A-Z]/.test(ch) ? String(ch.charCodeAt(0) - 55) : ch; for (const d of v) rest = (rest * 10 + Number(d)) % 97; }
  return rest === 1;
}
export class FirmAdapter {
  private ov: Record<string, Record<string, any>> = {};
  private drafts: { cards: any[]; apple: any[] } = { cards: [], apple: [] };
  // New cards / Apple Pay devices: a draft keeps its local id until the server has the row. `saving` = POST in flight,
  // `createdAs` = draft id → server id (stale renders still send the draft id), `gone` = removed while saving.
  private saving = new Set<string>();
  private createdAs: Record<string, string> = {};
  private gone = new Set<string>();
  private real = (id: string) => this.createdAs[id] || id;
  /** Rows created here stay at the end, in the order they were added (the server sorts by bank / digits: no jumping row). */
  private stable<T extends { id: string }>(rows: T[]): T[] {
    const mine = Object.values(this.createdAs), at = (id: string) => mine.indexOf(id);
    return [...rows.filter((r) => at(r.id) < 0), ...rows.filter((r) => at(r.id) >= 0).sort((a, b) => at(a.id) - at(b.id))];
  }
  private empDrafts: { id: string; name: string; fn: string }[] = [];
  private armedAt: Record<string, number> = {};
  private timers: Record<string, ReturnType<typeof setTimeout>> = {};
  private warned = new Set<string>();

  constructor(private notify: Notify) {}

  private warnOnce(key: string, text: string) {
    if (this.warned.has(key)) return;
    this.warned.add(key);
    this.notify(text, 'warning');
  }

  private fail = (e: unknown) => {
    this.notify(errorText(e), 'warning');
    void loadCore();
  };

  /** Debounced PATCH with a local overlay so the input keeps the typed value. */
  /** `inline`: a failed save also puts the error on the row (`err`, cards / Apple Pay); the typed values stay. */
  private later(key: string, patch: Record<string, any>, send: () => Promise<unknown>, inline = false) {
    if (inline) patch = { ...patch, err: '' };
    this.ov[key] = { ...(this.ov[key] || {}), ...patch };
    clearTimeout(this.timers[key]);
    this.timers[key] = setTimeout(() => {
      const sent = { ...this.ov[key] };
      send().then(() => loadCore()).then(() => {
        // Drop only what was saved; keep newer edits and other pending fields (e.g. a chosen role).
        const cur = this.ov[key] || {};
        for (const f of Object.keys(patch)) if (cur[f] === sent[f]) delete cur[f];
        if (!Object.keys(cur).length) delete this.ov[key];
      }, (e) => {
        if (inline) this.ov[key] = { ...(this.ov[key] || {}), err: errorText(e) };
        this.fail(e);
      });
    }, 700);
  }

  // ---------- derived view ----------

  roles() {
    return live.roles.map((r) => ({ id: r.id, label: r.name, locked: r.kind === 'SYSTEM', systemKey: r.systemKey, ...(this.ov['role:' + r.id] || {}) }));
  }

  isAdminRole(id: string) {
    return live.roles.some((r) => r.id === id && r.systemKey === 'admin');
  }

  perm2() {
    const P: Record<string, Record<string, string>> = {};
    for (const k of PERMISSION_KEYS) P[k] = {};
    for (const r of live.roles) for (const k of PERMISSION_KEYS) {
      const p = r.permissions.find((x) => x.key === k);
      P[k][r.id] = r.systemKey === 'admin' ? 'edit' : (p?.level || 'NONE').toLowerCase();
    }
    return P;
  }

  emps() {
    const real = live.employees.filter((e) => e.active !== false).map((e) => {
      const u = e.user;
      // With an account the e-mail is the login address; without one it is the address the invitation will go to.
      const base = { id: e.id, name: name(e), fn: e.function || '', mail: u?.email || e.email || '', userId: u?.id, hasAccount: !!u, accountActive: !!u?.active,
        access: u ? u.roles[0]?.roleId || 'none' : 'none', mustChange: !!u?.mustChangePassword, isDraft: false };
      return { ...base, ...(this.ov['emp:' + e.id] || {}) };
    });
    // New employees stay local until „Anlegen“ (name and function are required).
    return [...real, ...this.empDrafts.map((d) => ({ ...d, mail: '', userId: undefined, hasAccount: false, accountActive: false, access: 'none', mustChange: false, isDraft: true }))];
  }

  /**
   * Inactive and former employees („ausgetreten“): shown greyed after the active ones; they keep their documents and the
   * link to their (deactivated) account. No company cards for them.
   */
  formerEmps() {
    return live.employees.filter((e) => e.active === false).map((e) => ({ id: e.id, name: name(e), fn: e.function || '', mat: e.matricule || '', mail: e.user?.email || e.email || '',
      userId: e.user?.id, hasAccount: !!e.user, accountActive: !!e.user?.active, leftAt: e.leftAt ? String(e.leftAt).slice(0, 10) : null, ...(this.ov['emp:' + e.id] || {}) }));
  }

  /** Status: active / inactive / left (with the last day, „TT.MM.JJJJ“). Inactive or left deactivates the linked account. */
  setEmpStatus(e: { id: string; name: string }, status: 'active' | 'inactive' | 'left', day?: string) {
    const iso = day && /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(day.trim());
    if (status === 'left' && !iso) { this.ov['emp:' + e.id] = { ...(this.ov['emp:' + e.id] || {}), status: 'left', leftDay: day ?? '' }; return; }
    const body = status === 'active' ? { active: true } : status === 'inactive' ? { active: false, leftAt: null } : { leftAt: `${iso![3]}-${iso![2]}-${iso![1]}` };
    api('/core/employees/' + e.id, { method: 'PATCH', body }).then(() => {
      delete this.ov['emp:' + e.id];
      this.notify(`${e.name}: ${status === 'active' ? 'wieder aktiv' : status === 'inactive' ? 'inaktiv · Konto deaktiviert' : 'ausgetreten · Konto deaktiviert'}.`, 'success');
      return loadCore();
    }, (err) => {
      if (err?.code === 'employee_has_cards') { this.notify(e.name + ' hat noch Firmenkarten oder Apple-Pay-Geräte. Erst unter „Karten“ umhängen.', 'warning'); void loadCore(); } else this.fail(err);
    });
  }

  /** Link an existing account to the employee (later: the RimoID account); '' unlinks — the account itself stays. */
  linkAccount(e: { id: string; name: string }, userId: string) {
    api('/core/employees/' + e.id, { method: 'PATCH', body: { userId: userId || null } }).then(() => {
      this.notify(userId ? `${e.name}: Konto verbunden.` : `${e.name}: Verbindung zum Konto gelöst (das Konto bleibt bestehen).`, 'success');
      return loadCore();
    }, this.fail);
  }

  /** Accounts that belong to no employee (seed/system/test accounts): Firma · Autres comptes (BETA). */
  otherAccounts() {
    return live.users.filter((u) => !u.employee).map((u) => ({ id: u.id, name: `${u.firstName} ${u.lastName}`.trim() || u.email, email: u.email, active: u.active,
      mustChange: u.mustChangePassword, lastLoginAt: u.lastLoginAt, role: u.roles[0]?.roleId || '' }));
  }

  /** Second click within 5 s confirms a destructive action (window.confirm is unreliable in the desktop app). */
  armed(key: string) { return (this.armedAt[key] ?? 0) > Date.now() - 5000; }
  private confirm(key: string, text: string) {
    if (this.armed(key)) { delete this.armedAt[key]; return true; }
    this.armedAt[key] = Date.now();
    this.notify(text, 'warning', 5000);
    return false;
  }

  createDraft(id: string) {
    const d = this.empDrafts.find((x) => x.id === id);
    if (!d) return;
    const n = splitName(d.name);
    if (!n.firstName || !n.lastName) return this.notify('Vor- und Nachnamen eintragen.', 'warning');
    if (!d.fn.trim()) return this.notify('Funktion eintragen (Pflichtfeld).', 'warning');
    api('/core/employees', { method: 'POST', body: { ...n, function: d.fn.trim() } }).then(() => {
      this.empDrafts = this.empDrafts.filter((x) => x.id !== id);
      this.notify(`${d.name.trim()} angelegt.`, 'success');
      return loadCore();
    }, this.fail);
  }




  cards() {
    // Expired cards have their own section (expiredCards); they stay in the system for past payments and statements.
    const live_ = this.stable(live.cards.filter((k) => cardUsable(k))).map((k) => ({ id: k.id, last: k.last4, user: name(k.owner), via: `VISA (${k.bank})`, exp: expiryLabel(k.expiresAt), ...(this.ov['card:' + k.id] || {}) }));
    // A draft stays visible (same row, typed values, error) until the created card is in the live list.
    const shown = (d: any) => !this.createdAs[d.id] || !live.cards.some((c) => c.id === this.createdAs[d.id]);
    return [...live_, ...this.drafts.cards.filter(shown).map((d) => ({ ...d }))];
  }

  apple() {
    const out = this.stable(live.cards.filter((k) => cardUsable(k)).flatMap((k) => (k.instruments || []).filter((i) => i.active).map((i) => ({
      id: i.id, last: i.deviceCardLast4, device: i.deviceName, card: k.id, user: name(live.employees.find((e) => e.id === i.ownerId)),
      ...(this.ov['ap:' + i.id] || {}),
    }))));
    const shown = (d: any) => !this.createdAs[d.id] || !live.cards.some((k) => (k.instruments || []).some((i) => i.id === this.createdAs[d.id]));
    return [...out, ...this.drafts.apple.filter(shown).map((d) => ({ ...d, card: this.real(d.card) }))];
  }

  /** Active cards past their expiry (Firma → Karten „Abgelaufene Karten“); only the date can be changed (card renewed). */
  expiredCards() {
    return expiredCards(live.cards).map((k) => ({ id: k.id, last: k.last4, user: name(k.owner), via: `VISA (${k.bank})`, exp: expiryLabel(k.expiresAt), expired: true,
      devices: (k.instruments || []).filter((i) => i.active).length, ...(this.ov['card:' + k.id] || {}) }));
  }
  /** Expiry of an expired card changed (MM/JJ as on the card): saved once complete (or emptied). */
  setCardExpiry(id: string, exp: string) {
    const e = expiryMonth(exp);
    if (e === undefined) { this.ov['card:' + id] = { ...(this.ov['card:' + id] || {}), exp, err: '' }; return; }
    this.later('card:' + id, { exp }, () => api('/core/cards/' + id, { method: 'PATCH', body: { expiresAt: e } }), true);
  }

  escSup() {
    return live.suppliers.filter((s) => s.active && s.defaultDiscountRate != null)
      .map((s) => ({ id: s.id, sup: s.name, pct: pctIn(s.defaultDiscountRate), days: String(s.defaultDiscountDays ?? ''), ...(this.ov['sup:' + s.id] || {}) }));
  }

  absCustom() {
    return live.templates.filter((t) => t.active).sort((a, b) => a.sortOrder - b.sortOrder)
      .map((t) => ({ id: t.id, label: t.label, ...(this.ov['tpl:' + t.id] || {}) }));
  }

  accounts() {
    const a = (b: string) => live.accounts.find((x) => x.bank === b && x.active !== false);
    const bil = a('BIL'), bgl = a('BGL'), def = live.accounts.find((x) => x.defaultForPayment);
    return {
      bank: def?.bank || 'BIL',
      ibanBil: bil?.iban || '', bicBil: bil?.bic || '', holderBIL: bil?.holder || '',
      ibanBgl: bgl?.iban || '', bicBgl: bgl?.bic || '', holderBGL: bgl?.holder || '',
      ...(this.ov['acct'] || {}),
    };
  }

  /** The config object the design's firm()/role logic reads. */
  cfg(base: Record<string, any>) {
    return { ...base, emps: this.emps(), cards: this.cards(), cardsOld: this.expiredCards(), apple: this.apple(), escSup: this.escSup(), absCustom: this.absCustom(), roles: this.roles(), perm2: this.perm2(), ...this.accounts() };
  }

  // ---------- writes ----------

  set(patch: Record<string, any>) {
    for (const [k, v] of Object.entries(patch)) {
      if (k === 'emps') this.setEmps(v);
      else if (k === 'cards') this.setCards(v);
      else if (k === 'apple') this.setApple(v);
      else if (k === 'escSup') this.setEsc(v);
      else if (k === 'absCustom') this.setAbs(v);
      // roles / perm2: managed in the RimoID panel (the Rechte tab exists in mock mode only).
      else if (k === 'bank') this.setDefaultBank(v);
      else if (/^(iban|bic)(Bil|Bgl)$|^holder(BIL|BGL)$/.test(k)) this.setAccount(k, v);
    }
  }

  private setEmps(next: any[]) {
    const cur = this.emps();
    if (next.length > cur.length) {
      // „Mitarbeiter hinzufügen“: a local draft card; saved with „Anlegen“ once name and function are filled in.
      this.empDrafts.push({ id: 'draft:' + Date.now(), name: '', fn: '' });
      return;
    }
    if (next.length < cur.length) {
      const keep = new Set(next.map((m) => m.id));
      cur.filter((e) => !keep.has(e.id)).forEach((e) => {
        if (e.isDraft) { this.empDrafts = this.empDrafts.filter((d) => d.id !== e.id); return; }
        if (e.hasAccount && !this.confirm('emp:' + e.id, `Nochmal auf ✕ klicken: ${e.name} wird inaktiv, das Konto wird deaktiviert (nicht gelöscht).`)) return;
        api('/core/employees/' + e.id, { method: 'PATCH', body: { active: false } }).then(() => { this.notify(e.name + ' ist jetzt inaktiv' + (e.hasAccount ? ' · Konto deaktiviert.' : '.'), 'success'); return loadCore(); }, (err) => {
          if (err?.code === 'employee_has_cards') { this.notify(e.name + ' hat noch Firmenkarten oder Apple-Pay-Geräte. Erst unter „Karten“ umhängen.', 'warning'); void loadCore(); } else this.fail(err);
        });
      });
      return;
    }
    next.forEach((m, i) => {
      const o = cur[i];
      if (!o || o.id !== m.id) return;
      if (o.isDraft) {
        const d = this.empDrafts.find((x) => x.id === o.id);
        if (d) { d.name = m.name; d.fn = m.fn; }
        return;
      }
      if (m.access !== o.access) {
        // With an account: role change right away. Without: just remember the role for „Konto erstellen“.
        if (o.hasAccount) return this.warnOnce('access:' + o.id, 'Rollen werden im RimoID-Panel vergeben.');
        this.ov['emp:' + o.id] = { ...(this.ov['emp:' + o.id] || {}), access: m.access };
        return;
      }
      if (m.mail !== o.mail) {
        if (o.hasAccount) return this.warnOnce('mail:' + o.id, 'Die Login-E-Mail eines bestehenden Kontos lässt sich hier nicht ändern.');
        this.ov['emp:' + o.id] = { ...(this.ov['emp:' + o.id] || {}), mail: m.mail };
        if (isMail(m.mail)) this.later('emp:' + o.id, { mail: m.mail }, () => api('/core/employees/' + o.id, { method: 'PATCH', body: { email: m.mail.trim() } }));
        return;
      }
      if (m.name !== o.name || m.fn !== o.fn) {
        const n = splitName(m.name);
        this.ov['emp:' + o.id] = { ...(this.ov['emp:' + o.id] || {}), name: m.name, fn: m.fn };
        // Required: first and last name, function. Invalid input stays local until it is complete.
        if (!n.firstName || !n.lastName) return this.warnOnce('name:' + o.id, 'Vor- und Nachnamen eintragen · wird erst dann gespeichert.');
        if (!m.fn.trim()) return this.warnOnce('fn:' + o.id, 'Die Funktion ist Pflicht · wird erst dann gespeichert.');
        this.warned.delete('name:' + o.id); this.warned.delete('fn:' + o.id);
        this.later('emp:' + o.id, { name: m.name, fn: m.fn }, () => api('/core/employees/' + o.id, { method: 'PATCH', body: { ...n, function: m.fn.trim() } }));
      }
    });
  }


  // ---------- cards & Apple Pay ----------
  // A new row is created once it is complete (card: 4 digits, no duplicate; device: 4 digits, name ≥ 2 characters,
  // linked to a saved card), shortly after the last keystroke and only once. Changes made while the POST runs are sent
  // as a PATCH afterwards; a failed save keeps the row with its values and shows the error on it.

  private static bankOf = (via: string) => (String(via || '').includes('BGL') ? 'BGL' : 'BIL');
  private ownerId = (n: string) => live.employees.find((e) => name(e) === n)?.id ?? null;
  private cardBody(k: any) {
    // Expiry only when typed: complete MM/JJ → "YYYY-MM", emptied → null (only for a saved card that had one).
    const e = expiryMonth(k.exp), had = !!live.cards.find((c) => c.id === this.real(k.id))?.expiresAt;
    return { bank: FirmAdapter.bankOf(k.via), ownerId: this.ownerId(k.user), ...(/^\d{4}$/.test(k.last) ? { last4: k.last } : {}), ...(e || (e === null && had) ? { expiresAt: e } : {}) };
  }
  private appleBody(a: any) {
    return { ownerId: this.ownerId(a.user), ...(String(a.device || '').trim().length >= 2 ? { deviceName: a.device.trim() } : {}), ...(/^\d{4}$/.test(a.last) ? { deviceCardLast4: a.last } : {}) };
  }
  /** Same check as the row's warning („Diese Endziffern gibt es schon.“): no POST for a duplicate. */
  private cardDup(d: any) { return this.cards().some((c) => this.real(c.id) !== this.real(d.id) && c.last === d.last); }
  private cardReady(d: any) { return /^\d{4}$/.test(d.last) && !this.cardDup(d) && expiryMonth(d.exp) !== undefined; }
  private appleCard(a: any) { const id = this.real(a.card || ''); return live.cards.find((k) => k.id === id && cardUsable(k)) ?? null; }
  private appleReady(a: any) { return /^\d{4}$/.test(a.last) && String(a.device || '').trim().length >= 2 && !!this.appleCard(a); }
  private upsertDraft(list: 'cards' | 'apple', row: any, fields: string[]) {
    const all = this.drafts[list], i = all.findIndex((d) => d.id === row.id), prev = all[i];
    const same = !!prev && fields.every((f) => prev[f] === row[f]);
    const d = { ...row, err: same ? prev.err || '' : '' };
    this.drafts[list] = i >= 0 ? all.map((x, j) => (j === i ? d : x)) : [...all, d];
    return d;
  }
  private schedule(id: string, ready: () => boolean, create: () => void, ms: number) {
    clearTimeout(this.timers['new:' + id]);
    if (this.saving.has(id) || !ready()) return;
    this.timers['new:' + id] = setTimeout(create, ms);
  }

  private setCards(next: any[]) {
    const cur = this.cards(), keep = new Set(next.map((k) => this.real(k.id)));
    // removed → drafts disappear (a running POST is undone afterwards), saved cards are deactivated
    cur.filter((k) => !keep.has(this.real(k.id))).forEach((k) => {
      if (this.drafts.cards.some((d) => d.id === k.id)) {
        clearTimeout(this.timers['new:' + k.id]);
        this.drafts.cards = this.drafts.cards.filter((d) => d.id !== k.id);
        if (this.saving.has(k.id)) this.gone.add(k.id);
        return;
      }
      api('/core/cards/' + this.real(k.id), { method: 'PATCH', body: { active: false } }).then(() => loadCore(), this.fail);
    });
    for (const k of next) {
      const id = this.real(k.id), saved = live.cards.find((c) => c.id === id);
      if (saved) {
        if (!saved.active) continue;
        const o = cur.find((c) => this.real(c.id) === id);
        if (o && (o.last !== k.last || o.user !== k.user || o.via !== k.via || (o.exp || '') !== (k.exp || ''))) {
          // An expiry still being typed is only shown; it is saved once complete.
          if (expiryMonth(k.exp) === undefined) { this.ov['card:' + id] = { ...(this.ov['card:' + id] || {}), last: k.last, user: k.user, via: k.via, exp: k.exp }; continue; }
          this.later('card:' + id, { last: k.last, user: k.user, via: k.via, exp: k.exp || '' }, () => api('/core/cards/' + id, { method: 'PATCH', body: this.cardBody(k) }), true);
        }
        continue;
      }
      if (!this.drafts.cards.some((d) => d.id === k.id) && this.createdAs[k.id]) continue; // created, not loaded yet
      this.upsertDraft('cards', { id: k.id, last: k.last, user: k.user, via: k.via, exp: k.exp || '' }, ['last', 'user', 'via', 'exp']);
      this.schedule(k.id, () => this.cardReady(this.drafts.cards.find((d) => d.id === k.id)), () => this.createCard(k.id), 400);
    }
  }

  private createCard(id: string) {
    const d = this.drafts.cards.find((x) => x.id === id);
    if (!d || this.saving.has(id) || !this.cardReady(d)) return;
    this.saving.add(id);
    const sent = { ...d }, { bank, ownerId, last4, expiresAt } = this.cardBody(d) as { bank: string; ownerId: string | null; last4: string; expiresAt?: string | null };
    api<{ id: string }>('/core/cards', { method: 'POST', body: { bank, type: 'VISA', last4, ...(ownerId ? { ownerId } : {}), ...(expiresAt ? { expiresAt } : {}) } }).then(async (res) => {
      this.createdAs[id] = res.id;
      this.saving.delete(id);
      if (this.gone.delete(id)) { await api('/core/cards/' + res.id, { method: 'PATCH', body: { active: false } }).catch(() => undefined); this.drafts.cards = this.drafts.cards.filter((x) => x.id !== id); return loadCore(); }
      const now = this.drafts.cards.find((x) => x.id === id);
      if (now && (now.last !== sent.last || now.user !== sent.user || now.via !== sent.via || (now.exp || '') !== (sent.exp || '')))
        this.later('card:' + res.id, { last: now.last, user: now.user, via: now.via, exp: now.exp || '' }, () => api('/core/cards/' + res.id, { method: 'PATCH', body: this.cardBody(now) }), true);
      await loadCore();
      this.drafts.cards = this.drafts.cards.filter((x) => x.id !== id);
      // Apple Pay devices that waited for this card.
      for (const a of this.drafts.apple) if (this.real(a.card) === res.id) this.schedule(a.id, () => this.appleReady(a), () => this.createApple(a.id), 400);
    }, (e) => {
      this.saving.delete(id);
      if (this.gone.delete(id)) return;
      const now = this.drafts.cards.find((x) => x.id === id);
      if (now) {
        now.err = errorText(e);
        if (now.last !== sent.last || now.user !== sent.user || now.via !== sent.via || (now.exp || '') !== (sent.exp || '')) this.schedule(id, () => this.cardReady(now), () => this.createCard(id), 400);
      }
      this.notify(errorText(e), 'warning');
      void loadCore();
    });
  }

  private setApple(next: any[]) {
    const cur = this.apple(), keep = new Set(next.map((a) => this.real(a.id)));
    const instr = (id: string) => live.cards.find((k) => (k.instruments || []).some((i) => i.id === id));
    // removed → drafts disappear, saved devices are deactivated
    cur.filter((a) => !keep.has(this.real(a.id))).forEach((a) => {
      if (this.drafts.apple.some((d) => d.id === a.id)) {
        clearTimeout(this.timers['new:' + a.id]);
        this.drafts.apple = this.drafts.apple.filter((d) => d.id !== a.id);
        if (this.saving.has(a.id)) this.gone.add(a.id);
        return;
      }
      const k = instr(this.real(a.id));
      if (k) api('/core/cards/' + k.id + '/instruments/' + this.real(a.id), { method: 'PATCH', body: { active: false } }).then(() => loadCore(), this.fail);
    });
    for (const a of next) {
      const id = this.real(a.id), k = instr(id);
      if (k) {
        const o = cur.find((c) => this.real(c.id) === id);
        if (o && (o.last !== a.last || o.device !== a.device || o.user !== a.user || this.real(o.card) !== this.real(a.card))) {
          if (this.real(o.card) !== this.real(a.card)) { this.warnOnce('ap-move', 'Ein Apple-Pay-Gerät lässt sich nicht auf eine andere Karte umhängen · neu anlegen.'); continue; }
          this.later('ap:' + id, { last: a.last, device: a.device, user: a.user }, () => api('/core/cards/' + k.id + '/instruments/' + id, { method: 'PATCH', body: this.appleBody(a) }), true);
        }
        continue;
      }
      if (!this.drafts.apple.some((d) => d.id === a.id) && this.createdAs[a.id]) continue;
      this.upsertDraft('apple', { id: a.id, last: a.last, device: a.device, card: a.card, user: a.user }, ['last', 'device', 'card', 'user']);
      // Longer pause: the device name is typed letter by letter.
      this.schedule(a.id, () => this.appleReady(this.drafts.apple.find((d) => d.id === a.id)), () => this.createApple(a.id), 900);
    }
  }

  private createApple(id: string) {
    const d = this.drafts.apple.find((x) => x.id === id);
    if (!d || this.saving.has(id) || !this.appleReady(d)) return;
    const card = this.appleCard(d)!;
    this.saving.add(id);
    const sent = { ...d }, { ownerId, deviceName, deviceCardLast4 } = this.appleBody(d) as { ownerId: string | null; deviceName: string; deviceCardLast4: string };
    const changed = (n: any) => n.last !== sent.last || n.device !== sent.device || n.user !== sent.user;
    api<{ id: string }>('/core/cards/' + card.id + '/instruments', { method: 'POST', body: { deviceName, deviceCardLast4, ...(ownerId ? { ownerId } : {}) } }).then(async (res) => {
      this.createdAs[id] = res.id;
      this.saving.delete(id);
      if (this.gone.delete(id)) { await api('/core/cards/' + card.id + '/instruments/' + res.id, { method: 'PATCH', body: { active: false } }).catch(() => undefined); this.drafts.apple = this.drafts.apple.filter((x) => x.id !== id); return loadCore(); }
      const now = this.drafts.apple.find((x) => x.id === id);
      if (now && changed(now)) this.later('ap:' + res.id, { last: now.last, device: now.device, user: now.user }, () => api('/core/cards/' + card.id + '/instruments/' + res.id, { method: 'PATCH', body: this.appleBody(now) }), true);
      if (now && this.real(now.card) !== card.id) this.warnOnce('ap-move', 'Ein Apple-Pay-Gerät lässt sich nicht auf eine andere Karte umhängen · neu anlegen.');
      await loadCore();
      this.drafts.apple = this.drafts.apple.filter((x) => x.id !== id);
    }, (e) => {
      this.saving.delete(id);
      if (this.gone.delete(id)) return;
      const now = this.drafts.apple.find((x) => x.id === id);
      if (now) {
        now.err = errorText(e);
        if (changed(now) || this.real(now.card) !== card.id) this.schedule(id, () => this.appleReady(now), () => this.createApple(id), 900);
      }
      this.notify(errorText(e), 'warning');
      void loadCore();
    });
  }

  private setEsc(next: any[]) {
    const cur = this.escSup(), names = new Set(next.map((x) => x.sup));
    cur.filter((x) => !names.has(x.sup)).forEach((x) =>
      api('/core/suppliers/' + x.id, { method: 'PATCH', body: { defaultDiscountRate: null, defaultDiscountDays: null } }).then(() => loadCore(), this.fail));
    for (const x of next) {
      const o = cur.find((c) => c.sup === x.sup);
      const body = { defaultDiscountRate: pctOut(x.pct), defaultDiscountDays: /^\d+$/.test(x.days) ? Number(x.days) : null };
      if (!o) {
        const sup = live.suppliers.find((s) => s.name === x.sup);
        (sup ? api('/core/suppliers/' + sup.id, { method: 'PATCH', body }) : api('/core/suppliers', { method: 'POST', body: { name: x.sup, ...body } })).then(() => loadCore(), this.fail);
      } else if (o.pct !== x.pct || o.days !== x.days) {
        this.later('sup:' + o.id, { pct: x.pct, days: x.days }, () => (body.defaultDiscountRate ? api('/core/suppliers/' + o.id, { method: 'PATCH', body }) : Promise.resolve()));
      }
    }
  }

  private setAbs(next: any[]) {
    const cur = this.absCustom(), ids = new Set(next.map((x) => x.id));
    cur.filter((x) => !ids.has(x.id)).forEach((x) => api('/core/closing-checklist-templates/' + x.id, { method: 'PATCH', body: { active: false } }).then(() => loadCore(), this.fail));
    next.forEach((x, i) => {
      const o = cur.find((c) => c.id === x.id);
      if (!o) return void api('/core/closing-checklist-templates', { method: 'POST', body: { label: x.label, sortOrder: (i + 1) * 10 } }).then(() => loadCore(), this.fail);
      if (o.label !== x.label) this.later('tpl:' + o.id, { label: x.label }, () => api('/core/closing-checklist-templates/' + o.id, { method: 'PATCH', body: { label: x.label } }));
      if (cur.indexOf(o) !== i) api('/core/closing-checklist-templates/' + o.id, { method: 'PATCH', body: { sortOrder: (i + 1) * 10 } }).then(() => loadCore(), this.fail);
    });
  }



  // ---------- Vertrag Y: bank accounts (any number; BIL/BGL own cards, others generic) ----------
  private acctDrafts: Array<{ id: string; bank: 'OTHER'; bankName: string; iban: string; bic: string; holder: string }> = [];
  /** Active accounts (the standard bank first, then BIL, BGL, others by name) and new drafts at the end. */
  acctList() {
    const order = { BIL: 0, BGL: 1, OTHER: 2 } as const;
    const rows = live.accounts.filter((a) => a.active !== false)
      .sort((a, b) => Number(b.defaultForPayment) - Number(a.defaultForPayment) || order[a.bank] - order[b.bank] || (a.bankName || '').localeCompare(b.bankName || ''))
      .map((a) => ({ id: a.id, bank: a.bank, bankName: a.bankName || '', iban: a.iban, bic: a.bic || '', holder: a.holder, def: a.defaultForPayment, onDocs: !!a.onDocuments, draft: false, ...(this.ov['acct:' + a.id] || {}) }));
    return [...rows, ...this.acctDrafts.map((d) => ({ ...d, def: false, onDocs: false, draft: true, ...(this.ov['acct:' + d.id] || {}) }))];
  }
  /** Accounts on documents (≤ 3). */
  docsCount() { return live.accounts.filter((a) => a.active !== false && a.onDocuments).length; }
  addAccount(holder: string) {
    const id = 'new' + Date.now();
    this.acctDrafts.push({ id, bank: 'OTHER', bankName: '', iban: '', bic: '', holder });
    return id;
  }
  /** One field of a card: drafts are created once name, valid IBAN and holder are there; existing ones PATCH (debounced). */
  setAccountField(id: string, field: 'bankName' | 'iban' | 'bic' | 'holder', value: string) {
    const d = this.acctDrafts.find((x) => x.id === id);
    if (d) {
      (d as Record<string, string>)[field] = value;
      const iban = d.iban.replace(/\s/g, '').toUpperCase();
      if (field === 'iban' && iban.length >= 15 && !validIban(iban)) this.warnOnce('iban-' + iban, tr2('IBAN ungültig: Prüfziffer oder Länge stimmt nicht.', 'IBAN invalide : clé de contrôle ou longueur incorrecte.'));
      if (!d.bankName.trim() || !d.holder.trim() || !validIban(iban) || this.saving.has(id)) return;
      clearTimeout(this.timers['acct:' + id]);
      this.timers['acct:' + id] = setTimeout(() => {
        this.saving.add(id);
        api('/core/bank-accounts', { method: 'POST', body: { bank: 'OTHER', bankName: d.bankName.trim(), iban, bic: d.bic.replace(/\s/g, '').toUpperCase() || undefined, holder: d.holder.trim() } })
          .then(() => { this.acctDrafts = this.acctDrafts.filter((x) => x.id !== id); this.notify(tr2('Bankkonto angelegt.', 'Compte bancaire créé.'), 'success'); return loadCore(); }, this.fail)
          .finally(() => this.saving.delete(id));
      }, 900);
      return;
    }
    if (field === 'iban') { const iban = value.replace(/\s/g, '').toUpperCase(); if (iban.length < 15) { this.ov['acct:' + id] = { ...(this.ov['acct:' + id] || {}), iban: value }; return; } if (!validIban(iban)) { this.ov['acct:' + id] = { ...(this.ov['acct:' + id] || {}), iban: value }; return this.warnOnce('iban-' + iban, tr2('IBAN ungültig: Prüfziffer oder Länge stimmt nicht.', 'IBAN invalide : clé de contrôle ou longueur incorrecte.')); } }
    const body = field === 'holder' || field === 'bankName' ? value.replace(/\s+/g, ' ') : value.replace(/\s/g, '').toUpperCase();
    this.later('acct:' + id, { [field]: value }, () => api('/core/bank-accounts/' + id, { method: 'PATCH', body: { [field]: body } }));
  }
  /** „Auf Dokumenten“: at most 3 (the server answers 409 too_many_on_documents for a fourth). */
  toggleOnDocuments(id: string) {
    const a = live.accounts.find((x) => x.id === id); if (!a) return;
    if (!a.onDocuments && this.docsCount() >= 3) return this.notify(tr2('Höchstens 3 Konten auf Dokumenten.', 'Au maximum 3 comptes sur les documents.'), 'warning');
    api('/core/bank-accounts/' + id, { method: 'PATCH', body: { onDocuments: !a.onDocuments } }).then(() => loadCore(), this.fail);
  }
  makeDefaultAccount(id: string) {
    api('/core/bank-accounts/' + id + '/default', { method: 'POST' }).then(() => { this.notify(tr2('Standardbank festgelegt.', 'Banque par défaut définie.'), 'success'); return loadCore(); }, this.fail);
  }
  /** Remove: archived when it has statements or payments, else deleted; 6 s to undo (reactivate / create again). */
  removeAccount(id: string) {
    const d = this.acctDrafts.find((x) => x.id === id);
    if (d) { this.acctDrafts = this.acctDrafts.filter((x) => x.id !== id); this.notify(tr2('Entwurf verworfen.', 'Brouillon supprimé.'), 'success'); return; }
    const a = live.accounts.find((x) => x.id === id); if (!a) return;
    api<{ archived: boolean; deleted: boolean }>('/core/bank-accounts/' + id, { method: 'DELETE' }).then((r) => {
      void loadCore();
      const name = a.bank === 'OTHER' ? a.bankName || '' : a.bank;
      const undo = r.archived
        ? () => api('/core/bank-accounts/' + id, { method: 'PATCH', body: { active: true } }).then(() => loadCore(), this.fail)
        : () => api('/core/bank-accounts', { method: 'POST', body: { bank: a.bank, ...(a.bank === 'OTHER' ? { bankName: a.bankName } : {}), iban: a.iban, bic: a.bic || undefined, holder: a.holder, onDocuments: !!a.onDocuments, defaultForPayment: a.defaultForPayment } }).then(() => loadCore(), this.fail);
      this.notify(r.archived ? tr2(`${name} archiviert · bleibt für Auszüge und Zahlungen.`, `${name} archivé · reste pour les extraits et paiements.`) : tr2(`${name} gelöscht.`, `${name} supprimé.`), 'warning', 6000, undo);
    }, this.fail);
  }

  private setDefaultBank(bank: string) {
    const a = live.accounts.find((x) => x.bank === bank);
    if (a && !a.defaultForPayment) api('/core/bank-accounts/' + a.id, { method: 'PATCH', body: { defaultForPayment: true } }).then(() => loadCore(), this.fail);
  }

  private setAccount(key: string, value: string) {
    const bank = /Bil|BIL/.test(key) ? 'BIL' : 'BGL', a = live.accounts.find((x) => x.bank === bank);
    if (!a) return this.warnOnce('acct-missing', tr2('Für ' + bank + ' ist noch kein Konto angelegt.', 'Aucun compte n’est encore créé pour ' + bank + '.'));
    const field = key.startsWith('iban') ? 'iban' : key.startsWith('bic') ? 'bic' : 'holder';
    // F10: the IBAN is checked in the field already (country, length, check digits mod 97) – the server checks it again.
    if (field === 'iban') { const iban = value.replace(/\s/g, '').toUpperCase(); if (iban.length < 15) return; if (!validIban(iban)) return this.warnOnce('iban-' + iban, tr2('IBAN ungültig: Prüfziffer oder Länge stimmt nicht.', 'IBAN invalide : clé de contrôle ou longueur incorrecte.')); }
    this.later('acct', { [key]: value }, () => api('/core/bank-accounts/' + a.id, { method: 'PATCH', body: { [field]: value.replace(/\s/g, field === 'holder' ? ' ' : '') } }));
  }
}
